import { useEffect } from "react";
import { isTokenExist } from "utils/cookies";
import { useDispatch, useSelector } from "react-redux";
import store from "store";
import { setApp, setConfig, setLoadedInit } from "appredux/configSlice";
import { loadedInitSelector, userSelector } from "appredux/selectors";
import { useInits } from "queries/user";
import { setChat, setConversationType } from "appredux/chatSlice";
import { useParams } from "react-router";
import { includes } from "lodash-es";
import { INITS_PARAMS } from "config/inits";
import { generateBaseUri } from "utils/helper";
import {
  getAccessiblePhoneNumbers,
  resolveSelectedPhoneNumber,
} from "utils/phoneNumber";

// Digunakan untuk handle mengisi return endpoint dari init ke state redux
// useBootstrapEndpoint: khusus halaman pilih perusahaan — selama app.endpoint
// belum ada, inits ditembak ke app.bootstrapEndpoint (perusahaan pertama) karena
// inits di base URL awal 404. Tidak menyentuh app.endpoint / baseURL global.
const useLoadInits = (
  skipCheckEndpoint = false,
  { useBootstrapEndpoint = false } = {}
) => {
  const dispatch = useDispatch();
  const { id: phoneNumberIdParam } = useParams();
  const tokenExist = isTokenExist();
  const user = useSelector(userSelector);
  const loadedInit = useSelector(loadedInitSelector);
  const checkEndpoint = skipCheckEndpoint ? true : !!store.get("app.endpoint");
  const enabled = tokenExist && !user.id && !loadedInit && checkEndpoint;
  const bootstrapEndpoint =
    useBootstrapEndpoint && !store.get("app.endpoint")
      ? store.get("app.bootstrapEndpoint")
      : null;

  const { data } = useInits(
    {
      enabled,
    },
    INITS_PARAMS,
    bootstrapEndpoint
      ? { baseURL: generateBaseUri({ endpoint: bootstrapEndpoint }) }
      : {}
  );

  // Set redux from inits
  useEffect(() => {
    if (data?.user && enabled) {
      const websiteId = parseInt(data.website_id, 10);
      store.set("app.settings.locale", data.user?.lang);
      store.set("app.salutations", data.salutations || []);
      const findCompany = data?.companies?.find(
        (row) => row.website_id === websiteId
      );
      dispatch(
        setConfig({
          locale: data.user?.lang,
          endpoint: data.endpoint,
          selectedCompany: findCompany || {},
        })
      );
      const app = {
        user: { ...data.user },
        lang: data.lang,
        companies: data.companies || [],
        website_id: websiteId,
        setting: data.setting,
        phone_numbers: data.phone_numbers || [],
        phone_number_statuses: data.phone_number_statuses || {},
        agents: data.agents || [],
        error_codes: data.error_codes || [],
        templates: data.templates || [],
        selectedPhoneNumber: {},
        tags: data.tags || [],
        salutations: data.salutations || [],
        option: { ...data.option, settings: data.settings },
        topup_information: data.topup_information || {},
        reviewers: data.reviewers || [],
        billing: data.billing || [],
        ticket: data.ticket || {},
        announcement: data.announcement || "",
      };
      // Pilih channel dari url param jika boleh diakses, selain itu channel
      // pertama yang boleh diakses (aturan sama dengan sidebar)
      const isAssignMode = data.settings?.enable_auto_assignment_mode === "1";
      const accessiblePhoneNumbers = getAccessiblePhoneNumbers({
        phoneNumbers: app.phone_numbers,
        activeStatusId: app.phone_number_statuses.active,
        isAssignMode,
        isAdministrator: app.user.is_superuser,
      });
      app.selectedPhoneNumber =
        resolveSelectedPhoneNumber(accessiblePhoneNumbers, phoneNumberIdParam) ||
        {};

      if (isAssignMode) {
        dispatch(
          setConversationType(
            includes(app.selectedPhoneNumber?.roles, 1) ||
              app.user?.is_superuser
              ? "all"
              : "my_inbox"
          )
        );
      }
      dispatch(setApp(app));
      dispatch(
        setChat({
          lastStatusLogId: data.last_status_log_id,
          agent_online: data.agent_online,
        })
      );
      dispatch(setLoadedInit(true));
    }
  }, [data, dispatch, enabled, phoneNumberIdParam]);
};

export default useLoadInits;
